import * as HLF from "html-living-framework";
import { Draggable } from "./Draggable";
import Settings from "Data/Settings";

export class ConstrainedDraggable extends Draggable {

    /** set this to zero for infinite precision */
    public steps: number = 0;

    protected direction: PIXI.Point = new PIXI.Point();
    protected targetPos: PIXI.Point = new PIXI.Point();
    protected targetMagnitude: number = 0;
    protected snapToOriginPoint: boolean = false;

    protected progressCallback?: (progress: number) => void;

    protected _progress: number = 0;

    constructor(
        texture: string | PIXI.Texture,
        anchor: PIXI.IPointData = {x: 0.5, y: 0.5},
        pHoverVO: string,
        pProgressCallback: (progress: number) => void,
        pClickCallback: () => void,
    ) {
        super(texture, anchor, pHoverVO, pClickCallback);
        this.progressCallback = pProgressCallback;
    }

    /** this function assumes that the draggable's current position is one end of the constraint */
    public setEndPoint(pPoint: PIXI.Point) {
        this.targetPos = HLF.PointUtils.subtract(pPoint, this.position);
        const targetMagnitude = HLF.PointUtils.magnitude(this.targetPos);
        this.direction = HLF.PointUtils.multiply(this.targetPos, (1 / targetMagnitude));

        // not sure if this is a hack or if it's actually the correct solution...
        this.targetMagnitude = targetMagnitude / this.scale.x;
    }

    public setProgress(pPercent: number, pOptions: {dragging: boolean}) {
        pPercent = HLF.MathUtils.clamp(pPercent, 0, 1);
        // this could be a ternary statement but I'm expanding it for readability
        let snappedPercent: number;
        if (this.steps === 0) {
            snappedPercent = pPercent;
        }
        else {
            snappedPercent = Math.round(pPercent * this.steps) / this.steps;
        }

        if (pOptions.dragging === false) {
            this._progress = snappedPercent;
            const newPos = HLF.PointUtils.multiply(this.direction, snappedPercent * this.targetMagnitude);
            this._visuals.position.copyFrom(newPos);
        }
        else {
            this._progress = pPercent;
            const newPos = HLF.PointUtils.multiply(this.direction, pPercent * this.targetMagnitude);
            this._visuals.position.copyFrom(newPos);
        }
    }

    dragEnd() {
        super.dragEnd();
        if (this.snapToOriginPoint) {
            this.setProgress(0, {dragging: false});
            this.progressCallback?.(0);
        }
        else {
            this.setProgress(this._progress, {dragging: false});
            this.progressCallback?.(this._progress);
        }
    }

    public select(): void {
        // intentionally not calling super.select()
    }

    public deselect(): void {
        // intentionally not calling super.deselect()
    }

    protected snapToMouse(): void {
        // note that we do not call super.snapToMouse()
        // constrain position to line

        const localMousePosition = this._eventData!.getLocalPosition(this._visuals.parent);
        const dotProduct = this.dot(this.direction, localMousePosition);
        const sameDir: boolean = dotProduct > 0;
        let magnitude: number = HLF.PointUtils.magnitude(localMousePosition);

        if (sameDir) {
            magnitude = HLF.MathUtils.clamp(magnitude, 0, this.targetMagnitude);
        }
        else {
            magnitude = 0; // TODO: should this be something other than zero?
        }

        const progress = (magnitude / this.targetMagnitude);

        const previousProgress = this._progress;
        this.setProgress(progress, {dragging: true}); // follow mouse on drag, snap to nearest marker on end drag
        if (this._progress !== previousProgress) {
            this.progressCallback?.(this._progress);
        }

        if (progress >= 1) {
            this.deselect();
        }
    }

    protected dot(v1: PIXI.IPointData, v2: PIXI.IPointData): number {
        return v1.x * v2.x + v1.y * v2.y;
    }
}